import React, { useEffect, useRef, useState } from "react";
import { CMD, IMAGE_ALGORITHM, SUBSYS } from "../protocol/frameProtocol";
import {
  connectWebSerial,
  getWebSerialStatus,
  isWebSerialConnected,
  onWebSerialData,
  onWebSerialStatus,
  sendProtocolCommandViaWebSerial,
} from "../services/webSerial";
import FrameInspector from "./FrameInspector";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";
const IMAGE_WIDTH = 128;
const IMAGE_HEIGHT = 128;
const IMAGE_PIXELS = IMAGE_WIDTH * IMAGE_HEIGHT;

const panelStyle = {
  padding: 18,
  border: "1px solid rgba(255,255,255,.11)",
  borderRadius: 18,
  background: "rgba(255,255,255,.045)",
};

const inputStyle = {
  width: "100%",
  minHeight: 42,
  padding: "9px 11px",
  border: "1px solid rgba(255,255,255,.16)",
  borderRadius: 10,
  color: "#9ca3af",
  background: "#171717",
};

const buttonStyle = {
  minHeight: 46,
  padding: "10px 16px",
  border: "1px solid rgba(0,223,255,.4)",
  borderRadius: 11,
  color: "#00dfff",
  background: "rgba(0,223,255,.11)",
  fontWeight: 800,
  cursor: "pointer",
};

const sobelButtonStyle = {
  ...buttonStyle,
  border: "1px solid rgba(167,139,250,.48)",
  color: "#c4b5fd",
  background: "rgba(139,92,246,.13)",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "grid", gap: 6, color: "#9ca3af", fontSize: 13 }}>
      {label}
      {children}
    </label>
  );
}

function unpackImage(rawBytes) {
  if (rawBytes.length === IMAGE_PIXELS) {
    return Uint8ClampedArray.from(rawBytes);
  }

  if (rawBytes.length === IMAGE_PIXELS / 8) {
    const pixels = new Uint8ClampedArray(IMAGE_PIXELS);
    for (let index = 0; index < IMAGE_PIXELS; index += 1) {
      const byte = rawBytes[Math.floor(index / 8)];
      pixels[index] = byte & (0x80 >> (index % 8)) ? 255 : 0;
    }
    return pixels;
  }

  throw new Error(
    `Resultado de imagen con ${rawBytes.length} bytes; se esperaban ${IMAGE_PIXELS} en gris o ${IMAGE_PIXELS / 8} empacados a 1 bit.`
  );
}

export default function VistaPayload() {
  const [message, setMessage] = useState("");
  const [processing, setProcessing] = useState(null);
  const [webStatus, setWebStatus] = useState(getWebSerialStatus());
  const [progress, setProgress] = useState({ received: 0, total: 0 });
  const [imagePixels, setImagePixels] = useState(null);
  const [imageResult, setImageResult] = useState(null);
  const [lastCommand, setLastCommand] = useState(null);
  const assemblyRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const offStatus = onWebSerialStatus(setWebStatus);
    const offData = onWebSerialData((event) => {
      if (
        event.direction !== "frame" ||
        event.subsys !== SUBSYS.PAYLOAD ||
        event.cmd !== CMD.IMAGE_RESULT ||
        !event.details?.imageChunk
      ) {
        return;
      }

      const chunk = event.details.imageChunk;
      let assembly = assemblyRef.current;
      if (
        !assembly ||
        assembly.algorithm !== chunk.algorithm ||
        (assembly.requestSeq !== null && assembly.requestSeq !== chunk.requestSeq)
      ) {
        assembly = {
          requestSeq: chunk.requestSeq,
          algorithm: chunk.algorithm,
          algorithmName: chunk.algorithmName,
          totalChunks: chunk.totalChunks,
          chunks: new Map(),
        };
        assemblyRef.current = assembly;
      }

      if (!assembly.totalChunks) assembly.totalChunks = chunk.totalChunks;
      if (assembly.totalChunks !== chunk.totalChunks) {
        setMessage("La tarjeta cambió TOTAL_CHUNKS durante la misma imagen.");
        assemblyRef.current = null;
        return;
      }

      assembly.requestSeq = chunk.requestSeq;
      assembly.chunks.set(chunk.chunkIndex, Uint8Array.from(chunk.pixels));
      setProgress({ received: assembly.chunks.size, total: assembly.totalChunks });

      if (assembly.chunks.size !== assembly.totalChunks) return;

      try {
        const ordered = [];
        for (let index = 0; index < assembly.totalChunks; index += 1) {
          const bytes = assembly.chunks.get(index);
          if (!bytes) return;
          ordered.push(bytes);
        }
        const rawLength = ordered.reduce((total, bytes) => total + bytes.length, 0);
        const raw = new Uint8Array(rawLength);
        let offset = 0;
        ordered.forEach((bytes) => {
          raw.set(bytes, offset);
          offset += bytes.length;
        });

        setImagePixels(unpackImage(raw));
        setImageResult({
          algorithmName: assembly.algorithmName,
          rawBytes: raw.length,
          chunks: assembly.totalChunks,
          requestSeq: assembly.requestSeq,
        });
        setMessage(
          `Imagen ${assembly.algorithmName} reconstruida: ${IMAGE_WIDTH}×${IMAGE_HEIGHT}, ${raw.length} bytes en ${assembly.totalChunks} fragmentos.`
        );
      } catch (error) {
        setMessage(error.message);
      } finally {
        assemblyRef.current = null;
      }
    });

    return () => {
      offStatus();
      offData();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imagePixels) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const imageData = context.createImageData(IMAGE_WIDTH, IMAGE_HEIGHT);
    for (let index = 0; index < IMAGE_PIXELS; index += 1) {
      const value = imagePixels[index];
      const rgbaIndex = index * 4;
      imageData.data[rgbaIndex] = value;
      imageData.data[rgbaIndex + 1] = value;
      imageData.data[rgbaIndex + 2] = value;
      imageData.data[rgbaIndex + 3] = 255;
    }
    context.putImageData(imageData, 0, 0);
  }, [imagePixels]);

  const processImage = async (algorithm) => {
    const algorithmCode = IMAGE_ALGORITHM[algorithm];
    const algorithmName = algorithm === "THRESHOLD" ? "Umbral" : "Sobel";
    setProcessing(algorithm);
    setMessage(`Preparando procesamiento ${algorithmName}...`);
    setProgress({ received: 0, total: 0 });
    setImagePixels(null);
    setImageResult(null);
    assemblyRef.current = {
      requestSeq: null,
      algorithm: algorithmCode,
      algorithmName,
      totalChunks: 0,
      chunks: new Map(),
    };

    try {
      if (!isWebSerialConnected()) {
        await connectWebSerial({ baudRate: 115200 });
      }

      const result = await sendProtocolCommandViaWebSerial(
        API_BASE,
        {
          subsystem: "PAYLOAD",
          command: "PROCESS_IMAGE",
          itemId: 0x00,
          algorithm,
        },
        { expectedCommand: "IMAGE_RESULT", timeoutMs: 10000 }
      );
      setLastCommand(result);
      if (assemblyRef.current) assemblyRef.current.requestSeq = result.seq;
      setMessage(
        `Orden ${algorithmName} enviada (SEQ ${result.seq}). La respuesta aparece en Monitor Serial; esperando fragmentos de imagen.`
      );
    } catch (error) {
      assemblyRef.current = null;
      setMessage(`No se completó ${algorithmName}: ${error.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const progressPercent = progress.total
    ? Math.round((progress.received / progress.total) * 100)
    : 0;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <section style={panelStyle}>
        <strong style={{ color: "#00dfff", fontSize: 20 }}>Imagen interna del Payload</strong>
        <p style={{ color: "#b8c2cc", margin: "7px 0 0", lineHeight: 1.5 }}>
          El PIC ya contiene una imagen de 128×128 en memoria. La GUI sólo solicita que la procese;
          no configura ni captura desde una cámara.
        </p>
        <p style={{ color: webStatus.connected ? "#86efac" : "#facc15", margin: "9px 0 0", fontSize: 13 }}>
          {webStatus.connected
            ? "Tarjeta local conectada por Web Serial."
            : "Al procesar se solicitará el puerto Web Serial de la tarjeta local."}
        </p>
      </section>

      <section style={{ ...panelStyle, display: "grid", gap: 14 }}>
        <div>
          <strong style={{ color: "#fff", fontSize: 18 }}>Algoritmos ligeros</strong>
          <p style={{ color: "#b8c2cc", margin: "6px 0 0", lineHeight: 1.5 }}>
            Se envía PROCESAR (0x14) a PAYLOAD. RESULTADO (0x15) se registra y, si llega fragmentado con el formato provisional, la GUI reconstruye la imagen.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
          <button
            type="button"
            onClick={() => processImage("THRESHOLD")}
            disabled={Boolean(processing)}
            style={{ ...buttonStyle, opacity: processing ? 0.6 : 1 }}
          >
            {processing === "THRESHOLD" ? "Procesando Umbral..." : "Procesar imagen – Umbral"}
          </button>
          <button
            type="button"
            onClick={() => processImage("SOBEL")}
            disabled={Boolean(processing)}
            style={{ ...sobelButtonStyle, opacity: processing ? 0.6 : 1 }}
          >
            {processing === "SOBEL" ? "Procesando Sobel..." : "Procesar imagen – Sobel"}
          </button>
        </div>

        {progress.total > 0 && (
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ color: "#b8c2cc", fontSize: 13 }}>
              Recibiendo imagen: {progress.received}/{progress.total} fragmentos ({progressPercent}%)
            </div>
            <div style={{ height: 8, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,.1)" }}>
              <div style={{ width: `${progressPercent}%`, height: "100%", background: "#00dfff" }} />
            </div>
          </div>
        )}

        {lastCommand?.frameHex && <FrameInspector hex={lastCommand.frameHex} direction="TX" label={`PROCESAR · SEQ ${lastCommand.seq}`} />}
        {message && <p style={{ margin: 0, color: "#9cecff", lineHeight: 1.45 }}>{message}</p>}
        <p style={{ margin: 0, color: "#94a3b8", fontSize: 12 }}>
          El detalle exacto del payload de 0x14/0x15/0x16 aún debe confirmarse con firmware. Por ahora se envía [00, ALGORITHM] y la respuesta hexadecimal siempre queda en Monitor Serial.
        </p>
      </section>

      <section style={{ ...panelStyle, display: "grid", gap: 14 }}>
        <div>
          <strong style={{ color: "#fff", fontSize: 18 }}>Resultado reconstruido</strong>
          <p style={{ color: "#b8c2cc", margin: "6px 0 0", lineHeight: 1.5 }}>
            Compatible con 16,384 bytes en escala de grises o 2,048 bytes binarios empacados.
          </p>
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
          <canvas
            ref={canvasRef}
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            aria-label="Resultado de imagen procesada"
            style={{
              width: 320,
              maxWidth: "100%",
              aspectRatio: "1 / 1",
              imageRendering: "pixelated",
              border: "1px solid rgba(0,223,255,.35)",
              borderRadius: 10,
              background: "#080b0f",
            }}
          />
          <div style={{ color: "#b8c2cc", lineHeight: 1.65, fontSize: 13 }}>
            {imageResult ? (
              <>
                <div>Algoritmo: {imageResult.algorithmName}</div>
                <div>Tamaño: {IMAGE_WIDTH}×{IMAGE_HEIGHT}</div>
                <div>Datos: {imageResult.rawBytes} bytes</div>
                <div>Fragmentos: {imageResult.chunks}</div>
                <div>REQUEST_SEQ: {imageResult.requestSeq}</div>
              </>
            ) : (
              "La imagen aparecerá aquí cuando lleguen todos los fragmentos válidos."
            )}
          </div>
        </div>
      </section>

      <section style={{ ...panelStyle, opacity: 0.62 }}>
        <div style={{ marginBottom: 14 }}>
          <strong style={{ color: "#facc15", fontSize: 18 }}>Trabajo futuro: cámara externa</strong>
          <p style={{ color: "#b8c2cc", margin: "6px 0 0", lineHeight: 1.5 }}>
            Estos controles se conservan como referencia, pero están deshabilitados y no generan tramas.
          </p>
        </div>
        <fieldset disabled style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 13 }}>
            <Field label="Pines"><input value="" readOnly style={inputStyle} /></Field>
            <Field label="Formato de imagen"><select value="RAW" readOnly style={inputStyle}><option>RAW</option></select></Field>
            <Field label="Resolución"><select value="128x128" readOnly style={inputStyle}><option>128x128</option></select></Field>
            <Field label="Protocolo"><select value="SPI" readOnly style={inputStyle}><option>SPI</option><option>UART</option></select></Field>
            <Field label="Modo SPI"><input value="0" readOnly style={inputStyle} /></Field>
            <Field label="Frecuencia SPI"><input value="" readOnly style={inputStyle} /></Field>
            <Field label="Baud rate UART"><input value="115200" readOnly style={inputStyle} /></Field>
            <Field label="Bits por palabra"><input value="8" readOnly style={inputStyle} /></Field>
          </div>
          <button type="button" disabled style={{ ...buttonStyle, cursor: "not-allowed" }}>
            Enviar configuración de cámara externa
          </button>
        </fieldset>
      </section>
    </div>
  );
}
